import { UserTransaction } from '@features/api/response/UserTransactions';
import { SelectChangeEvent } from '@mui/material';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { amountFilterRanges } from './Transactions.consts';
import {
  AmountRange,
  AmountRangeKeys,
  FilterCriteria,
} from './Transactions.types';

export const useTransactionsFilter = (
  initialTransactions: UserTransaction[]
) => {
  const [filteredData, setFilteredData] = useState<UserTransaction[]>([
    ...initialTransactions,
  ]);

  const [filterCriteria, setFilterCriteria] = useState<FilterCriteria>();
  const [isSearchButtonEnabled, setIsSearchButtonEnabled] =
    useState<boolean>(true);

  useEffect(() => {
    if (initialTransactions.length > 0)
      setFilteredData(() => [...initialTransactions]);
  }, [initialTransactions]);

  const handleFilterChange = (
    value: string | Date | AmountRange,
    key: keyof FilterCriteria
  ) => {
    setFilterCriteria({ ...filterCriteria, [key]: value });
    setIsSearchButtonEnabled(true);
  };

  const handleProductFilterChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value;
    handleFilterChange(value as string, 'productName');
  };

  const handleTypeFilterChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value;
    handleFilterChange(value as string, 'type');
  };

  const handleDateFromFilterChange = (value: Date) => {
    handleFilterChange(value as Date, 'subscribedDateFrom');
  };

  const handleDateToFilterChange = (value: Date) => {
    handleFilterChange(value as Date, 'subscribedDateTo');
  };

  const handleAmountRangeFilterChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as AmountRangeKeys;
    handleFilterChange(value, 'amountRange');
  };

  const filterData = () => {
    let filteredDataTemp = [...initialTransactions];
    if (filterCriteria?.productName) {
      filteredDataTemp = filteredDataTemp.filter(
        (x) => x.productName === filterCriteria.productName
      );
    }
    if (filterCriteria?.type) {
      filteredDataTemp = filteredDataTemp.filter(
        (x) => x.type === filterCriteria.type
      );
    }
    if (filterCriteria?.subscribedDateFrom) {
      filteredDataTemp = filteredDataTemp.filter((x) => {
        return (
          moment(x.subscriptionDate, 'YYYY-MM-DD').unix() >=
          moment(filterCriteria.subscribedDateFrom, 'DD/MM/YYYY').unix()
        );
      });
    }
    if (filterCriteria?.subscribedDateTo) {
      filteredDataTemp = filteredDataTemp.filter((x) => {
        return (
          moment(x.subscriptionDate, 'YYYY-MM-DD').unix() <=
          moment(filterCriteria.subscribedDateTo, 'DD/MM/YYYY').unix()
        );
      });
    }

    if (filterCriteria?.amountRange) {
      const amountRange = amountFilterRanges[filterCriteria.amountRange];
      filteredDataTemp = filteredDataTemp.filter(
        (x) =>
          x.amount >= (amountRange?.from ?? 0) &&
          x.amount < (amountRange?.to ?? Math.min())
      );
    }

    setFilteredData(filteredDataTemp);
  };

  const handleSearchButtonClick = () => {
    filterData();
    setIsSearchButtonEnabled(false);
  };

  const handleClearButtonClick = () => {
    setFilterCriteria({});
    setFilteredData([...initialTransactions]);
  };

  const [availableProducts, availableTypes] = useMemo(() => {
    if (!initialTransactions) return [];

    const products: string[] = [];
    const types: string[] = [];
    initialTransactions.forEach((x) => {
      if (!products.includes(x.productName)) products.push(x.productName);
      if (!types.includes(x.type)) types.push(x.type);
    });

    return [
      products.map((x) => ({ value: x, name: x })),
      types.map((x) => ({ value: x, name: x })),
    ];
  }, [initialTransactions]);

  return {
    values: {
      availableProducts,
      availableTypes,
      filterCriteria,
      filteredData,
      isSearchButtonEnabled,
    },
    handlers: {
      handleProductFilterChange,
      handleTypeFilterChange,
      handleDateFromFilterChange,
      handleDateToFilterChange,
      handleAmountRangeFilterChange,
      handleSearchButtonClick,
      handleClearButtonClick,
    },
  };
};
